import type { IdNameAddressResult } from '@/api/useMetadata/types'
import { InfoIcon } from '@/components/_SiteWide/InfoIcon'
import PlusOutlined from '@ant-design/icons/lib/icons/PlusOutlined'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { DatePicker, Form, Popover, Select, TimePicker, type FormInstance } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { disableFutureTimePickerDates, getDetourPayload } from '../util'
import type { AddDetourFormValues } from '../types'

type AddNoLoadProps = {
  driverName: string
  driverScheduleId: string
  terminalOptions: IdNameAddressResult[] | undefined
  handleAddDetour: (detour: Gravitate.OrderDetour) => void
}

type NoLoadPopoverContentProps = AddNoLoadProps & {
  form: FormInstance<AddDetourFormValues>
  onClose: () => void
}

export function AddNoLoad({ driverName, driverScheduleId, terminalOptions, handleAddDetour }: AddNoLoadProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [form] = Form.useForm<AddDetourFormValues>()

  const handleClose = () => {
    setIsOpen(false)
    form.resetFields() // Reset form when popover closes
  }

  return (
    <Popover
      content={
        <NoLoadPopoverContent
          form={form}
          driverName={driverName}
          driverScheduleId={driverScheduleId}
          terminalOptions={terminalOptions}
          handleAddDetour={handleAddDetour}
          onClose={handleClose}
        />
      }
      title='Add Terminal No Load'
      trigger='click'
      placement='topLeft'
      styles={{ root: { width: 260 } }}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        // Reset form when popover closes (from any method)
        if (!open) {
          form.resetFields()
        }
      }}
    >
      <GraviButton color='primary' appearance='outline' buttonText='Add No Load' icon={<PlusOutlined />} />
    </Popover>
  )
}

function NoLoadPopoverContent({
  form,
  driverName,
  driverScheduleId,
  terminalOptions,
  handleAddDetour,
  onClose,
}: NoLoadPopoverContentProps) {
  function handleFinish() {
    const formValues = form.getFieldsValue()
    const payload = getDetourPayload(formValues, driverName, driverScheduleId)
    handleAddDetour(payload)
    onClose() // Close the popover after successful submission (form will be reset by parent)
  }

  return (
    <Form form={form} className='pt-3 pb-1' layout='vertical' onFinish={handleFinish}>
      <Form.Item name='location' label='Terminal' rules={[{ required: true, message: 'Please select a terminal' }]}>
        <Select
          placeholder='Select Terminal'
          options={terminalOptions?.map((option) => ({ label: option.name, value: JSON.stringify(option) })) || []}
          showSearch
        />
      </Form.Item>
      <Horizontal gap={8} justifyContent='space-between'>
        <Form.Item name='date' label='Date' rules={[{ required: true, message: 'Please select a date' }]}>
          <DatePicker placeholder='Select Date' disabledDate={(current) => current && current > dayjs().endOf('day')} />
        </Form.Item>
        <Form.Item
          name='time'
          label={
            <Texto>
              Time
              <InfoIcon className='ml-2' tooltipTitle={`Enter time in dispatcher's timezone`} />
            </Texto>
          }
          rules={[{ required: true, message: 'Please select a time' }]}
        >
          <TimePicker
            showSecond={false}
            format={'h:mm A'}
            placeholder='Select Time'
            disabledTime={() => {
              const selectedDate = form.getFieldValue('date')
              return disableFutureTimePickerDates(selectedDate)
            }}
          />
        </Form.Item>
      </Horizontal>
      <Horizontal justifyContent='flex-end' gap={12}>
        <GraviButton appearance='outlined' buttonText='Cancel' onClick={onClose} />
        <GraviButton buttonText='Confirm' appearance='solid' theme2 htmlType='submit' />
      </Horizontal>
    </Form>
  )
}
